import { differenceInMonths, addMonths } from 'date-fns';

export type TargetType = 'price' | 'date' | 'savings' | 'payment';
export type VariableType = 'price' | 'date' | 'savings' | 'income' | 'interest' | 'downPayment';
export type StepDirection = 'both' | 'up' | 'down';

export interface Variable {
  type: VariableType;
  value: number;
  step: number;
  stepDirection?: StepDirection; // Optional for backward compatibility
  label: string;
}

export interface KPIResult {
  maxPrice: number;
  monthlyPayment: number;
  purchaseDate: Date | null;
  requiredSavings: number;
  interestRate: number;
  mortgageAmount: number;
  downPayment: number;
  initialDownPayment: number;
  additionalSavings: number;
  savingPeriodMonths: number;
  targetPrice: number;
  downPaymentPercentage: number;
  totalInterestPaid: number;
}

export class CalculatorEngine {
  private readonly MIN_DOWN_PAYMENT_RATIO = 0.03;
  private readonly MAX_DOWN_PAYMENT_RATIO = 0.95;
  private readonly MAX_AFFORDABILITY_ITERATIONS = 5;
  private readonly AFFORDABILITY_CONVERGENCE_THRESHOLD = 1000;

  private calculatePropertyTax(price: number): number {
    if (price <= 300000) return price * 0.0125 / 12;
    if (price <= 500000) return price * 0.0110 / 12;
    if (price <= 750000) return price * 0.0100 / 12;
    if (price <= 1000000) return price * 0.0085 / 12;
    if (price <= 1500000) return price * 0.0075 / 12;
    if (price <= 2000000) return price * 0.0065 / 12;
    if (price <= 3000000) return price * 0.0060 / 12;
    return price * 0.0050 / 12;
  }

  private calculateHomeInsurance(price: number): number {
    if (price <= 300000) return price * 0.0035 / 12;
    if (price <= 500000) return price * 0.0032 / 12;
    if (price <= 750000) return price * 0.0030 / 12;
    if (price <= 1000000) return price * 0.0028 / 12;
    if (price <= 1500000) return price * 0.0025 / 12;
    if (price <= 2000000) return price * 0.0023 / 12;
    if (price <= 3000000) return price * 0.0021 / 12;
    return price * 0.0020 / 12;
  }

  private calculateUtilities(price: number): number {
    return Math.min(500, 200 + Math.floor(price / 500000) * 50);
  }

  private calculateMortgageInsurance(mortgageAmount: number, downPaymentRatio: number): number {
    return downPaymentRatio < 0.2 ? mortgageAmount * 0.005 / 12 : 0;
  }

  private calculateMonthlyPayment(principal: number, annualRate: number, years: number): number {
    if (principal <= 0 || annualRate <= 0 || years <= 0) return 0;
    
    const monthlyRate = annualRate / 12 / 100;
    const numberOfPayments = years * 12;
    
    try {
      const payment = principal *
        (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
        (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

      return Math.round(payment);
    } catch (error) {
      return 0;
    }
  }

  private calculateTotalInterest(principal: number, monthlyPayment: number, annualRate: number, years: number): number {
    if (principal <= 0 || monthlyPayment <= 0 || annualRate <= 0 || years <= 0) return 0;
    
    const monthlyRate = annualRate / 12 / 100;
    const numberOfPayments = years * 12;
    let balance = principal;
    let totalInterest = 0;
    
    for (let i = 0; i < numberOfPayments && balance > 0; i++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = Math.min(monthlyPayment - interestPayment, balance);
      
      totalInterest += interestPayment;
      balance = Math.max(0, balance - principalPayment);
      
      if (balance === 0) break;
    }
    
    return Math.round(totalInterest);
  }

  private calculateMaxMortgageFromIncome(
    monthlyIncome: number,
    interestRate: number,
    years: number,
    debtServiceRatio: number,
    downPayment: number
  ): number {
    if (monthlyIncome <= 0) return 0;

    const maxMonthlyBudget = monthlyIncome * debtServiceRatio;
    const monthlyRate = interestRate / 12 / 100;
    const numberOfPayments = years * 12;

    let estimatedPrice = 0;
    let previousPrice = 0;

    for (let iteration = 0; iteration < this.MAX_AFFORDABILITY_ITERATIONS; iteration++) {
      const propertyTax = this.calculatePropertyTax(estimatedPrice);
      const homeInsurance = this.calculateHomeInsurance(estimatedPrice);
      const utilities = this.calculateUtilities(estimatedPrice);

      const budgetForMortgageAndMI = maxMonthlyBudget - propertyTax - homeInsurance - utilities;

      if (budgetForMortgageAndMI <= 0) return 0;

      let maxMortgage: number;
      try {
        maxMortgage = budgetForMortgageAndMI *
          (Math.pow(1 + monthlyRate, numberOfPayments) - 1) /
          (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments));
      } catch (error) {
        return 0;
      }

      estimatedPrice = maxMortgage + downPayment;
      const downPaymentRatio = downPayment / estimatedPrice;
      const mortgageInsurance = this.calculateMortgageInsurance(maxMortgage, downPaymentRatio);

      const actualBudgetForMortgage = budgetForMortgageAndMI - mortgageInsurance;

      if (actualBudgetForMortgage <= 0) return 0;

      try {
        maxMortgage = actualBudgetForMortgage *
          (Math.pow(1 + monthlyRate, numberOfPayments) - 1) /
          (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments));
      } catch (error) {
        return 0;
      }

      estimatedPrice = maxMortgage + downPayment;

      if (iteration > 0 && Math.abs(estimatedPrice - previousPrice) < this.AFFORDABILITY_CONVERGENCE_THRESHOLD) {
        return Math.round(maxMortgage);
      }

      previousPrice = estimatedPrice;
    }

    const finalMortgage = estimatedPrice - downPayment;
    return Math.round(finalMortgage);
  }

  private calculateStepValue(
    type: VariableType,
    variable: Variable,
    step: number
  ): number {
    const direction = variable.stepDirection || 'both';
    let stepMultiplier = 0;
    
    // Calculate the step multiplier based on direction
    if (direction === 'both') {
      // Standard step calculation: -1, 0, 1
      stepMultiplier = step - 1;
    } else if (direction === 'up') {
      // For 'up' direction: 0, 1, 2
      stepMultiplier = step;
    } else if (direction === 'down') {
      // For 'down' direction: -2, -1, 0
      stepMultiplier = step - 2;
    }
    
    // Calculate value with step multiplier
    const valueWithStep = variable.value + (stepMultiplier * variable.step);
    
    // Special handling for date type
    if (type === 'date') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowTimestamp = tomorrow.getTime();
      
      // Ensure date is never before tomorrow
      return Math.max(valueWithStep, tomorrowTimestamp);
    }
    
    // For non-date types, ensure the value is not negative
    return Math.max(0, valueWithStep);
  }

  private getValue(
    type: VariableType,
    rowVariables: Variable[],
    columnVariables: Variable[],
    allValues: Record<VariableType, Variable>,
    rowStep?: number,
    colStep?: number
  ): number {
    // Check if it's a row variable
    const rowVar = rowVariables.find(v => v.type === type);
    if (rowVar && rowStep !== undefined) {
      return this.calculateStepValue(type, rowVar, rowStep);
    }
    
    // Check if it's a column variable
    const colVar = columnVariables.find(v => v.type === type);
    if (colVar && colStep !== undefined) {
      return this.calculateStepValue(type, colVar, colStep);
    }
    
    // If it's neither a row nor column variable, return the base value
    return allValues[type].value;
  }

  private calculatePriceTarget(
    _monthlyIncome: number,
    currentDownPayment: number,
    _monthlySavings: number,
    interestRate: number,
    targetDate: Date,
    today: Date,
    _maxMortgage: number,
    years: number,
    _debtServiceRatio: number
  ): KPIResult {
    const monthsToTarget = Math.max(0, differenceInMonths(targetDate, today));
    const totalSavings = _monthlySavings * monthsToTarget;
    const totalDownPayment = currentDownPayment + totalSavings;
    
    // Calculate maximum price based on down payment and mortgage capacity
    const maxPrice = _maxMortgage + totalDownPayment;
    
    // Calculate actual mortgage needed (could be 0 if down payment covers full price)
    const mortgageAmount = maxPrice > totalDownPayment ? Math.min(_maxMortgage, maxPrice - totalDownPayment) : 0;
    
    // Calculate monthly payment only if there is a mortgage
    const monthlyPayment = mortgageAmount > 0 ? 
      this.calculateMonthlyPayment(mortgageAmount, interestRate, years) : 0;
    
    // Calculate total interest only if there is a mortgage
    const totalInterestPaid = mortgageAmount > 0 ?
      this.calculateTotalInterest(mortgageAmount, monthlyPayment, interestRate, years) : 0;

    return {
      maxPrice,
      mortgageAmount,
      monthlyPayment,
      purchaseDate: targetDate,
      requiredSavings: _monthlySavings,
      interestRate,
      downPayment: totalDownPayment,
      initialDownPayment: currentDownPayment,
      additionalSavings: totalSavings,
      savingPeriodMonths: monthsToTarget,
      targetPrice: maxPrice,
      downPaymentPercentage: maxPrice > 0 ? (totalDownPayment / maxPrice) * 100 : 0,
      totalInterestPaid
    };
  }

  private calculatePaymentTarget(
    _monthlyIncome: number,
    currentDownPayment: number,
    _monthlySavings: number,
    interestRate: number,
    targetPrice: number,
    targetDate: Date,
    today: Date,
    _maxMortgage: number,
    years: number
  ): KPIResult {
    // Calculate the down payment available at purchase time
    const monthsToTarget = Math.max(0, differenceInMonths(targetDate, today));
    const additionalSavings = _monthlySavings * monthsToTarget;
    const totalDownPayment = currentDownPayment + additionalSavings;
    
    // Calculate the mortgage amount (don't cap by affordability for this view)
    const mortgageAmount = Math.max(0, targetPrice - totalDownPayment);
    
    // Calculate monthly payment based on the mortgage amount
    // Unlike other calculations, we don't limit by affordability ratio
    const monthlyPayment = mortgageAmount > 0 ?
      this.calculateMonthlyPayment(mortgageAmount, interestRate, years) : 0;

    // Calculate total interest paid
    const totalInterestPaid = mortgageAmount > 0 ?
      this.calculateTotalInterest(mortgageAmount, monthlyPayment, interestRate, years) : 0;

    return {
      maxPrice: targetPrice,
      mortgageAmount,
      monthlyPayment,
      purchaseDate: targetDate,
      requiredSavings: _monthlySavings,
      interestRate,
      downPayment: totalDownPayment,
      initialDownPayment: currentDownPayment,
      additionalSavings,
      savingPeriodMonths: monthsToTarget,
      targetPrice,
      downPaymentPercentage: targetPrice > 0 ? (totalDownPayment / targetPrice) * 100 : 0,
      totalInterestPaid
    };
  }

  private calculateDateTarget(
    _monthlyIncome: number,
    currentDownPayment: number,
    _monthlySavings: number,
    interestRate: number,
    targetPrice: number,
    today: Date,
    maxMortgage: number,
    years: number,
    _debtServiceRatio: number
  ): KPIResult {
    // Calculate minimum required down payment
    const minDownPayment = Math.max(
      targetPrice * this.MIN_DOWN_PAYMENT_RATIO,
      targetPrice - maxMortgage
    );
    
    // Instead of always using PREFERRED_DOWN_PAYMENT_RATIO (20%),
    // dynamically adjust based on affordability and debt service ratio
    // Higher debt service ratio = lower down payment percentage needed
    const dynamicDownPaymentPct = Math.max(
      this.MIN_DOWN_PAYMENT_RATIO,
      Math.min(
        this.MAX_DOWN_PAYMENT_RATIO,
        (targetPrice - maxMortgage) / targetPrice
      )
    );
    
    // Use the dynamically calculated down payment percentage
    const preferredDownPayment = Math.max(
      minDownPayment,
      targetPrice * dynamicDownPaymentPct
    );

    // If current savings are sufficient for down payment
    if (currentDownPayment >= preferredDownPayment) {
      const mortgageAmount = targetPrice - currentDownPayment;
      const monthlyPayment = mortgageAmount > 0 ?
        this.calculateMonthlyPayment(mortgageAmount, interestRate, years) : 0;
      const totalInterestPaid = mortgageAmount > 0 ?
        this.calculateTotalInterest(mortgageAmount, monthlyPayment, interestRate, years) : 0;

      return {
        maxPrice: targetPrice,
        mortgageAmount,
        monthlyPayment,
        purchaseDate: today,
        requiredSavings: _monthlySavings,
        interestRate,
        downPayment: currentDownPayment,
        initialDownPayment: currentDownPayment,
        additionalSavings: 0,
        savingPeriodMonths: 0,
        targetPrice,
        downPaymentPercentage: (currentDownPayment / targetPrice) * 100,
        totalInterestPaid
      };
    }

    // If no monthly savings, purchase isn't possible
    if (_monthlySavings <= 0) {
      return {
        maxPrice: targetPrice,
        mortgageAmount: 0,
        monthlyPayment: 0,
        purchaseDate: null,
        requiredSavings: _monthlySavings,
        interestRate,
        downPayment: currentDownPayment,
        initialDownPayment: currentDownPayment,
        additionalSavings: 0,
        savingPeriodMonths: 0,
        targetPrice,
        downPaymentPercentage: (currentDownPayment / targetPrice) * 100,
        totalInterestPaid: 0
      };
    }

    // Calculate months needed to reach down payment
    const remainingAmount = preferredDownPayment - currentDownPayment;
    const monthsNeeded = Math.ceil(remainingAmount / _monthlySavings);
    
    // If it takes too long, purchase isn't feasible
    if (monthsNeeded > 360) { // 30 years
      return {
        maxPrice: targetPrice,
        mortgageAmount: 0,
        monthlyPayment: 0,
        purchaseDate: null,
        requiredSavings: _monthlySavings,
        interestRate,
        downPayment: currentDownPayment,
        initialDownPayment: currentDownPayment,
        additionalSavings: 0,
        savingPeriodMonths: 0,
        targetPrice,
        downPaymentPercentage: (currentDownPayment / targetPrice) * 100,
        totalInterestPaid: 0
      };
    }
    
    const purchaseDate = addMonths(today, monthsNeeded);
    const additionalSavings = _monthlySavings * monthsNeeded;
    const totalDownPayment = currentDownPayment + additionalSavings;
    const mortgageAmount = targetPrice - totalDownPayment;
    const monthlyPayment = this.calculateMonthlyPayment(mortgageAmount, interestRate, years);
    const totalInterestPaid = this.calculateTotalInterest(mortgageAmount, monthlyPayment, interestRate, years);

    return {
      maxPrice: targetPrice,
      mortgageAmount,
      monthlyPayment,
      purchaseDate,
      requiredSavings: _monthlySavings,
      interestRate,
      downPayment: totalDownPayment,
      initialDownPayment: currentDownPayment,
      additionalSavings,
      savingPeriodMonths: monthsNeeded,
      targetPrice,
      downPaymentPercentage: (totalDownPayment / targetPrice) * 100,
      totalInterestPaid
    };
  }

  private calculateSavingsTarget(
    _monthlyIncome: number,
    currentDownPayment: number,
    _monthlySavings: number,
    interestRate: number,
    targetPrice: number,
    targetDate: Date,
    today: Date,
    maxMortgage: number,
    years: number,
    _debtServiceRatio: number
  ): KPIResult {
    const monthsToTarget = Math.max(0, differenceInMonths(targetDate, today));
    
    // If target date is in the past or present, purchase isn't possible
    if (targetDate <= today) {
      return {
        maxPrice: targetPrice,
        mortgageAmount: 0,
        monthlyPayment: 0,
        purchaseDate: null,
        requiredSavings: Infinity,
        interestRate,
        downPayment: currentDownPayment,
        initialDownPayment: currentDownPayment,
        additionalSavings: 0,
        savingPeriodMonths: 0,
        targetPrice,
        downPaymentPercentage: (currentDownPayment / targetPrice) * 100,
        totalInterestPaid: 0
      };
    }
    
    // Calculate minimum required down payment
    const minRequiredPct = targetPrice * this.MIN_DOWN_PAYMENT_RATIO;
    
    // Instead of always using PREFERRED_DOWN_PAYMENT_RATIO (20%),
    // dynamically adjust based on affordability and debt service ratio
    // Higher debt service ratio = lower down payment percentage needed
    const dynamicDownPaymentPct = Math.max(
      this.MIN_DOWN_PAYMENT_RATIO,
      Math.min(
        this.MAX_DOWN_PAYMENT_RATIO,
        (targetPrice - maxMortgage) / targetPrice
      )
    );
    
    // Use the dynamically calculated down payment percentage
    const requiredDownPayment = Math.max(
      minRequiredPct,
      Math.max(0, targetPrice * dynamicDownPaymentPct)
    );
    
    // Calculate required monthly savings
    const remainingDownPayment = Math.max(0, requiredDownPayment - currentDownPayment);
    const requiredSavings = remainingDownPayment > 0 ? Math.ceil(remainingDownPayment / monthsToTarget) : 0;
    
    // Calculate mortgage details
    const totalDownPayment = currentDownPayment + requiredSavings * monthsToTarget;
    const mortgageAmount = Math.max(0, targetPrice - totalDownPayment);
    const monthlyPayment = mortgageAmount > 0 ? 
      this.calculateMonthlyPayment(mortgageAmount, interestRate, years) : 0;
    const totalInterestPaid = mortgageAmount > 0 ?
      this.calculateTotalInterest(mortgageAmount, monthlyPayment, interestRate, years) : 0;

    return {
      maxPrice: targetPrice,
      mortgageAmount,
      monthlyPayment,
      purchaseDate: targetDate,
      requiredSavings,
      interestRate,
      downPayment: totalDownPayment,
      initialDownPayment: currentDownPayment,
      additionalSavings: requiredSavings * monthsToTarget,
      savingPeriodMonths: monthsToTarget,
      targetPrice,
      downPaymentPercentage: (totalDownPayment / targetPrice) * 100,
      totalInterestPaid
    };
  }

  generateResults(
    target: TargetType,
    rowVariables: Variable[],
    columnVariables: Variable[],
    allValues: Record<VariableType, Variable>,
    settings: { amortizationYears: number, debtServiceRatio: number }
  ): KPIResult[][] {
    if (!rowVariables.length) return [];

    const calculateKPIs = (rowStep: number, colStep: number): KPIResult => {
      const monthlyIncome = this.getValue('income', rowVariables, columnVariables, allValues, rowStep, colStep) / 12;
      const currentDownPayment = this.getValue('downPayment', rowVariables, columnVariables, allValues, rowStep, colStep);
      const monthlySavings = this.getValue('savings', rowVariables, columnVariables, allValues, rowStep, colStep);
      const interestRate = this.getValue('interest', rowVariables, columnVariables, allValues, rowStep, colStep);
      const targetPrice = this.getValue('price', rowVariables, columnVariables, allValues, rowStep, colStep);
      const targetDate = new Date(this.getValue('date', rowVariables, columnVariables, allValues, rowStep, colStep));
      
      const today = new Date();
      const maxMortgage = this.calculateMaxMortgageFromIncome(monthlyIncome, interestRate, settings.amortizationYears, settings.debtServiceRatio, currentDownPayment);

      switch (target) {
        case 'price':
          return this.calculatePriceTarget(
            monthlyIncome, currentDownPayment, monthlySavings, interestRate, 
            targetDate, today, maxMortgage, settings.amortizationYears,
            settings.debtServiceRatio
          );
          
        case 'date':
          return this.calculateDateTarget(
            monthlyIncome, currentDownPayment, monthlySavings, interestRate,
            targetPrice, today, maxMortgage, settings.amortizationYears,
            settings.debtServiceRatio
          );
          
        case 'savings':
          return this.calculateSavingsTarget(
            monthlyIncome, currentDownPayment, monthlySavings, interestRate,
            targetPrice, targetDate, today, maxMortgage, settings.amortizationYears,
            settings.debtServiceRatio
          );
          
        case 'payment':
          return this.calculatePaymentTarget(
            monthlyIncome, currentDownPayment, monthlySavings, interestRate,
            targetPrice, targetDate, today, maxMortgage, settings.amortizationYears
          );
      }
    };

    // Get row and column steps based on variables and their step directions
    const getStepsArray = (variables: Variable[]): number[] => {
      if (variables.length === 0) return [0];
      
      // For the main variable (first in the array)
      const mainVar = variables[0];
      const direction = mainVar.stepDirection || 'both';
      
      if (direction === 'both') {
        return [0, 1, 2]; // -1, 0, +1 after adjustment in calculations
      } else if (direction === 'up') {
        return [0, 1, 2]; // 0, +1, +2 after adjustment
      } else if (direction === 'down') {
        return [0, 1, 2]; // -2, -1, 0 after adjustment
      }
      
      return [0, 1, 2]; // Default fallback
    };

    // Get the appropriate steps based on the variable direction
    const rowSteps = getStepsArray(rowVariables);
    const colSteps = getStepsArray(columnVariables);

    // Generate the grid of results using the appropriate steps
    const resultsGrid: KPIResult[][] = [];
    
    for (let rowIdx = 0; rowIdx < rowSteps.length; rowIdx++) {
      const row: KPIResult[] = [];
      
      for (let colIdx = 0; colIdx < (columnVariables.length > 0 ? colSteps.length : 1); colIdx++) {
        const result = calculateKPIs(rowSteps[rowIdx], colSteps[colIdx]);
        row.push(result);
      }
      
      resultsGrid.push(row);
    }

    return this.validateGridConsistency(resultsGrid, target, rowVariables, columnVariables);
  }

  // New method for direct calculation without stepping
  calculateDirectResult(
    target: TargetType,
    values: Record<VariableType, Variable>,
    settings: { amortizationYears: number, debtServiceRatio: number }
  ): KPIResult {
    const monthlyIncome = values.income.value / 12;
    const currentDownPayment = values.downPayment.value;
    const monthlySavings = values.savings.value;
    const interestRate = values.interest.value;
    const targetPrice = values.price.value;
    const targetDate = new Date(values.date.value);
    
    const today = new Date();
    const maxMortgage = this.calculateMaxMortgageFromIncome(monthlyIncome, interestRate, settings.amortizationYears, settings.debtServiceRatio, currentDownPayment);

    // Call the appropriate calculation function based on the target
    switch (target) {
      case 'price':
        return this.calculatePriceTarget(
          monthlyIncome, currentDownPayment, monthlySavings, interestRate, 
          targetDate, today, maxMortgage, settings.amortizationYears,
          settings.debtServiceRatio
        );
        
      case 'date':
        return this.calculateDateTarget(
          monthlyIncome, currentDownPayment, monthlySavings, interestRate,
          targetPrice, today, maxMortgage, settings.amortizationYears,
          settings.debtServiceRatio
        );
        
      case 'savings':
        return this.calculateSavingsTarget(
          monthlyIncome, currentDownPayment, monthlySavings, interestRate,
          targetPrice, targetDate, today, maxMortgage, settings.amortizationYears,
          settings.debtServiceRatio
        );
        
      case 'payment':
        return this.calculatePaymentTarget(
          monthlyIncome, currentDownPayment, monthlySavings, interestRate,
          targetPrice, targetDate, today, maxMortgage, settings.amortizationYears
        );
    }
  }

  private validateGridConsistency(
    grid: KPIResult[][],
    target: TargetType,
    rowVariables: Variable[],
    columnVariables: Variable[]
  ): KPIResult[][] {
    const validatedGrid = JSON.parse(JSON.stringify(grid)) as KPIResult[][];
    const rowType = rowVariables[0]?.type;
    const colType = columnVariables[0]?.type;

    // Validate each row
    for (let row = 0; row < validatedGrid.length; row++) {
      for (let col = 1; col < validatedGrid[row].length; col++) {
        const currentCell = validatedGrid[row][col];
        const previousCell = validatedGrid[row][col - 1];

        if (!currentCell || !previousCell) continue;

        switch (target) {
          case 'date': {
            if (currentCell.purchaseDate && previousCell.purchaseDate) {
              if (colType === 'price' || colType === 'interest') {
                const currentDate = new Date(currentCell.purchaseDate).getTime();
                const prevDate = new Date(previousCell.purchaseDate).getTime();
                if (currentDate < prevDate) {
                  currentCell.purchaseDate = previousCell.purchaseDate;
                  currentCell.savingPeriodMonths = previousCell.savingPeriodMonths;
                  currentCell.additionalSavings = currentCell.requiredSavings * currentCell.savingPeriodMonths;
                  currentCell.downPayment = currentCell.initialDownPayment + currentCell.additionalSavings;
                }
              }
            }
            break;
          }

          case 'price': {
            if (colType === 'income' || colType === 'downPayment') {
              if (currentCell.maxPrice < previousCell.maxPrice) {
                currentCell.maxPrice = previousCell.maxPrice;
                currentCell.mortgageAmount = currentCell.maxPrice - currentCell.downPayment;
                currentCell.monthlyPayment = this.calculateMonthlyPayment(
                  currentCell.mortgageAmount,
                  currentCell.interestRate,
                  30
                );
                currentCell.totalInterestPaid = this.calculateTotalInterest(
                  currentCell.mortgageAmount,
                  currentCell.monthlyPayment,
                  currentCell.interestRate,
                  30
                );
              }
            }
            break;
          }

          case 'savings': {
            if (colType === 'price') {
              if (currentCell.requiredSavings < previousCell.requiredSavings) {
                currentCell.requiredSavings = previousCell.requiredSavings;
                currentCell.additionalSavings = currentCell.requiredSavings * currentCell.savingPeriodMonths;
                currentCell.downPayment = currentCell.initialDownPayment + currentCell.additionalSavings;
              }
            }
            break;
          }
        }
      }
    }

    // Validate each column
    for (let col = 0; col < validatedGrid[0].length; col++) {
      for (let row = 1; row < validatedGrid.length; row++) {
        const currentCell = validatedGrid[row][col];
        const previousCell = validatedGrid[row - 1][col];

        if (!currentCell || !previousCell) continue;

        switch (target) {
          case 'date': {
            if (currentCell.purchaseDate && previousCell.purchaseDate) {
              if (rowType === 'savings') {
                const currentDate = new Date(currentCell.purchaseDate).getTime();
                const prevDate = new Date(previousCell.purchaseDate).getTime();
                if (currentDate > prevDate) {
                  currentCell.purchaseDate = previousCell.purchaseDate;
                  currentCell.savingPeriodMonths = previousCell.savingPeriodMonths;
                  currentCell.additionalSavings = currentCell.requiredSavings * currentCell.savingPeriodMonths;
                  currentCell.downPayment = currentCell.initialDownPayment + currentCell.additionalSavings;
                }
              }
            }
            break;
          }

          case 'price': {
            if (rowType === 'income' || rowType === 'downPayment') {
              if (currentCell.maxPrice < previousCell.maxPrice) {
                currentCell.maxPrice = previousCell.maxPrice;
                currentCell.mortgageAmount = currentCell.maxPrice - currentCell.downPayment;
                currentCell.monthlyPayment = this.calculateMonthlyPayment(
                  currentCell.mortgageAmount,
                  currentCell.interestRate,
                  30
                );
                currentCell.totalInterestPaid = this.calculateTotalInterest(
                  currentCell.mortgageAmount,
                  currentCell.monthlyPayment,
                  currentCell.interestRate,
                  30
                );
              }
            }
            break;
          }

          case 'savings': {
            if (rowType === 'price') {
              if (currentCell.requiredSavings < previousCell.requiredSavings) {
                currentCell.requiredSavings = previousCell.requiredSavings;
                currentCell.additionalSavings = currentCell.requiredSavings * currentCell.savingPeriodMonths;
                currentCell.downPayment = currentCell.initialDownPayment + currentCell.additionalSavings;
              }
            }
            break;
          }
        }
      }
    }

    return validatedGrid;
  }
}